import './index.scss';

import personPlusIcon from 'url:@assets/icons/person.plus.plain.svg';

import PersonCard from "@components/PersonCard";
import VolunteerTeam, {
  VolunteerTeamBundle,
  VolunteerTeamMemeber,
  VolunteerTeamRole
} from '@data/interfaces/Volunteering/volunteerteam.interface';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { RoutedPerson, personPagePath } from '@pages/v2/People/PersonPage';
import InviteVolunteerModal from './InviteVolunteerModal';
import VolunteerContextMenu from '@components/VolunteerContextMenu';
import RemoveVolunteerModal, { RemoveVolunteerTarget } from './RemoveVolunteerModal';
import ManageRolesModal from './ManageRolesModal';
import EditMemberRolesModal, { MemberRolesTarget } from './EditMemberRolesModal';
import EditTeamModal from './EditTeamModal';

type VolunteerMenuState = {
  personID: string;
  personName: string;
  teamID: string;
  teamName: string;
  roleIDs: string[];
  x: number;
  y: number;
};

const MENU_EXIT_DURATION_MS = 140;

/**
 * How many volunteers the teams overview shows per team before pointing at the
 * team's own page. Six fills whole rows at one, two, and three columns.
 */
export const TEAM_PREVIEW_LIMIT = 6;

// Top level rather than under /volunteering. `/team` on its own redirects to
// the staff page at /staff, where it used to live.
export function teamPagePath(teamID: string) {
  return `/team/${teamID}`;
}

/**
 * - `overview`: every team, each capped at TEAM_PREVIEW_LIMIT volunteers, with
 *   its name linking to the team's page.
 * - `team-page`: a single team with every volunteer. The page titles itself
 *   with the team name, so the section doesn't repeat it.
 */
export type TeamsWorkspaceVariant = 'overview' | 'team-page';

// Everything the teams overview and a team's own page share: the team
// sections, and the modals and context menu their actions open. Both pages
// render this, so a change to how a team is managed lands in both.
export default function TeamsWorkspace(props: {
  bundles: VolunteerTeamBundle[],
  refetch: () => void | Promise<void>,
  variant: TeamsWorkspaceVariant
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { bundles, refetch } = props;

  // Looked up at render rather than captured when a modal opens, so a modal
  // sees roles added or renamed while it's open.
  const bundleFor = (teamID?: string | null) => {
    return bundles.find((bundle) => bundle.team.id === teamID) ?? null;
  };

  const rolesFor = (teamID?: string | null): VolunteerTeamRole[] => {
    return bundleFor(teamID)?.roles ?? [];
  };

  // Back from the person's page returns to whichever page this is hosted on.
  const openPerson = (personID: string, personName: string) => {
    const state: RoutedPerson = { personName, backLocation: location.pathname };
    navigate(personPagePath(personID), { state });
  };

  const [teamForInvite, setTeamForInvite] = useState<VolunteerTeam | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const closeInviteModal = () => {
    setIsInviteModalOpen(false);

    window.setTimeout(() => {
      setTeamForInvite(null);
    }, 200);
  };

  const openInviteModal = (team: VolunteerTeam) => {
    setTeamForInvite(team);
    setIsInviteModalOpen(true);
  };

  // Keyed per open, so the modal re-seeds from the team's latest roles.
  const [teamForRoles, setTeamForRoles] = useState<VolunteerTeam | null>(null);
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [rolesModalKey, setRolesModalKey] = useState(0);

  const openRolesModal = (team: VolunteerTeam) => {
    setTeamForRoles(team);
    setRolesModalKey((currentKey) => currentKey + 1);
    setIsRolesModalOpen(true);
  };

  // Keyed per open, so the name field starts from the team's current name.
  const [teamForEdit, setTeamForEdit] = useState<VolunteerTeam | null>(null);
  const [isEditTeamOpen, setIsEditTeamOpen] = useState(false);
  const [editTeamKey, setEditTeamKey] = useState(0);

  const openEditTeam = (team: VolunteerTeam) => {
    setTeamForEdit(team);
    setEditTeamKey((currentKey) => currentKey + 1);
    setIsEditTeamOpen(true);
  };

  // Also keyed per open: reopening for the same volunteer after a save should
  // start from what they hold now, not what was picked last time.
  const [memberRolesTarget, setMemberRolesTarget] = useState<MemberRolesTarget | null>(null);
  const [isMemberRolesOpen, setIsMemberRolesOpen] = useState(false);
  const [memberRolesKey, setMemberRolesKey] = useState(0);

  // The menu stays mounted through its exit animation, so the selected and
  // displayed states are tracked separately.
  const [selectedMenu, setSelectedMenu] = useState<VolunteerMenuState | null>(null);
  const [displayedMenu, setDisplayedMenu] = useState<VolunteerMenuState | null>(null);
  const [isClosingMenu, setIsClosingMenu] = useState(false);

  useEffect(() => {
    if (selectedMenu) {
      setDisplayedMenu(selectedMenu);
      setIsClosingMenu(false);
      return;
    }

    if (!displayedMenu) {
      setIsClosingMenu(false);
      return;
    }

    setIsClosingMenu(true);

    const timeout = window.setTimeout(() => {
      setDisplayedMenu(null);
      setIsClosingMenu(false);
    }, MENU_EXIT_DURATION_MS);

    return () => window.clearTimeout(timeout);
  }, [selectedMenu, displayedMenu]);

  const [removalTarget, setRemovalTarget] = useState<RemoveVolunteerTarget | null>(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);

  const closeRemoveModal = () => {
    setIsRemoveModalOpen(false);

    window.setTimeout(() => {
      setRemovalTarget(null);
    }, 200);
  };

  return <>
    <InviteVolunteerModal
      key={teamForInvite?.id ?? 'invite-volunteer'}
      isOpen={isInviteModalOpen}
      team={teamForInvite}
      roles={rolesFor(teamForInvite?.id)}
      close={closeInviteModal}
      onInvited={() => {
        closeInviteModal();
        void refetch();
      }}
    />

    <ManageRolesModal
      key={`manage-roles-${rolesModalKey}`}
      isOpen={isRolesModalOpen}
      team={teamForRoles}
      roles={rolesFor(teamForRoles?.id)}
      members={bundleFor(teamForRoles?.id)?.members ?? []}
      close={() => setIsRolesModalOpen(false)}
      onChanged={() => {
        void refetch();
      }}
    />

    <EditTeamModal
      key={`edit-team-${editTeamKey}`}
      isOpen={isEditTeamOpen}
      team={teamForEdit}
      close={() => setIsEditTeamOpen(false)}
      onSaved={() => {
        setIsEditTeamOpen(false);
        void refetch();
      }}
    />

    <EditMemberRolesModal
      key={`member-roles-${memberRolesKey}`}
      isOpen={isMemberRolesOpen}
      target={memberRolesTarget}
      roles={rolesFor(memberRolesTarget?.teamID)}
      close={() => setIsMemberRolesOpen(false)}
      onSaved={() => {
        setIsMemberRolesOpen(false);
        void refetch();
      }}
      onManageRoles={() => {
        setIsMemberRolesOpen(false);

        const bundle = bundleFor(memberRolesTarget?.teamID);

        if (bundle) {
          openRolesModal(bundle.team);
        }
      }}
    />

    {displayedMenu ? <VolunteerContextMenu
      personName={displayedMenu.personName}
      x={displayedMenu.x}
      y={displayedMenu.y}
      isClosing={isClosingMenu}
      onRequestClose={() => setSelectedMenu(null)}
      onDetails={() => {
        setSelectedMenu(null);
        openPerson(displayedMenu.personID, displayedMenu.personName);
      }}
      onEditRoles={() => {
        setSelectedMenu(null);
        setMemberRolesTarget({
          personID: displayedMenu.personID,
          personName: displayedMenu.personName,
          teamID: displayedMenu.teamID,
          teamName: displayedMenu.teamName,
          roleIDs: displayedMenu.roleIDs
        });
        setMemberRolesKey((currentKey) => currentKey + 1);
        setIsMemberRolesOpen(true);
      }}
      onAssignedTraining={() => {
        setSelectedMenu(null);
        // The names ride along so the detail page's header is right before its
        // own requests come back.
        navigate(`/volunteering/teams/${displayedMenu.teamID}/people/${displayedMenu.personID}`, {
          state: {
            personName: displayedMenu.personName,
            teamName: displayedMenu.teamName
          }
        });
      }}
      onRemoveFromTeam={() => {
        setSelectedMenu(null);
        setRemovalTarget({
          personID: displayedMenu.personID,
          personName: displayedMenu.personName,
          teamID: displayedMenu.teamID,
          teamName: displayedMenu.teamName
        });
        setIsRemoveModalOpen(true);
      }}
    /> : null}

    <RemoveVolunteerModal
      key={removalTarget ? `${removalTarget.teamID}-${removalTarget.personID}` : 'remove-volunteer'}
      isOpen={isRemoveModalOpen}
      target={removalTarget}
      close={closeRemoveModal}
      onRemoved={() => {
        closeRemoveModal();
        void refetch();
      }}
    />

    {bundles.map((team) => (
      <TeamSection
        key={team.team.id}
        bundle={team}
        variant={props.variant}
        onPersonClick={(member, event) => {
          setSelectedMenu({
            personID: member.personID,
            personName: member.attributes.personName || i18next.t('volunteering:team.unknownPerson'),
            teamID: team.team.id,
            teamName: team.team.attributes.name,
            roleIDs: member.attributes.roleIDs ?? [],
            x: event.clientX + window.scrollX,
            y: event.clientY + window.scrollY
          });
        }}
        onRolesClick={openRolesModal}
        onEditClick={openEditTeam}
        onInviteClick={openInviteModal} />
    ))}
  </>
}

// The names of the roles a member holds, alphabetised, for their card. An ID
// that no longer resolves -- a role deleted since this load -- is skipped.
function roleSummary(member: VolunteerTeamMemeber, roleNameByID: Map<string, string>) {
  return (member.attributes.roleIDs ?? [])
    .map((roleID) => roleNameByID.get(roleID))
    .filter((name): name is string => Boolean(name))
    .sort((one, two) => one.localeCompare(two))
    .join(', ');
}

function TeamSection(
  props: {
    bundle: VolunteerTeamBundle,
    variant: TeamsWorkspaceVariant,
    onPersonClick: (member: VolunteerTeamMemeber, event: React.MouseEvent<HTMLButtonElement>) => void,
    onRolesClick: (team: VolunteerTeam) => void,
    onEditClick: (team: VolunteerTeam) => void,
    onInviteClick: (team: VolunteerTeam) => void
  }
) {
  const navigate = useNavigate();
  const { t } = useTranslation('volunteering');
  const team = props.bundle.team;
  const isOverview = props.variant === 'overview';

  const members = props.bundle.members;
  const sorted = useMemo(() => {
    return [...members].sort((a, b) => {
      const nameA = a.attributes.personName || '';
      const nameB = b.attributes.personName || '';
      return nameA.localeCompare(nameB);
    });
  }, [members]);

  const roles = props.bundle.roles;
  const roleNameByID = useMemo(() => {
    return new Map((roles ?? []).map((role) => [role.id, role.attributes.name]));
  }, [roles]);

  const visible = isOverview ? sorted.slice(0, TEAM_PREVIEW_LIMIT) : sorted;
  const hiddenCount = sorted.length - visible.length;

  // The name rides along so the team page's header is right before its own
  // request comes back.
  const openTeamPage = () => {
    navigate(teamPagePath(team.id), { state: { teamName: team.attributes.name } });
  };

  return <div className='team-section'>
    <TeamHeader
      leading={isOverview ?
        <h2>
          <button type='button' className='team-name-link' onClick={openTeamPage}>
            {team.attributes.name}
          </button>
        </h2> :
        null}
      onRolesClick={() => props.onRolesClick(team)}
      onEditClick={() => props.onEditClick(team)}
      onInviteClick={() => props.onInviteClick(team)} />

    {sorted.length === 0 && <p className='no-members'>{t('team.noMembers')}</p>}

    <div className="people">
      {visible.map((member) => (
        <PersonCard
          key={member.personID}
          name={member.attributes.personName || t('team.unknownPerson')}
          // The subtitle shows the member's roles rather than an email, which
          // not every membership carries.
          email={roleSummary(member, roleNameByID)}
          onClick={(event) => {
            props.onPersonClick(member, event);
          }}
        />
      ))}
    </div>

    {hiddenCount > 0 && (
      <button type='button' className='view-all-button' onClick={openTeamPage}>
        {t('team.viewAll', { count: sorted.length })}
        <ChevronForwardIcon />
      </button>
    )}
  </div>
}

function TeamHeader(props: {
  /** The team name on the overview; nothing on the team's page. */
  leading: React.ReactNode,
  onRolesClick: () => void,
  onEditClick: () => void,
  onInviteClick: () => void
}) {
  const { t } = useTranslation('volunteering');

  return <div className='head'>
    {props.leading}

    <div className='head-actions'>
      <button type='button' className='icon-action' aria-label={t('team.roles')} title={t('team.roles')} onClick={props.onRolesClick}>
        <TagIcon />
      </button>

      <button type='button' className='icon-action' aria-label={t('team.editTeam')} title={t('team.editTeam')} onClick={props.onEditClick}>
        <PencilIcon />
      </button>


      <button type='button' className='plus-button' aria-label={t('team.inviteVolunteer')} title={t('team.inviteVolunteer')} onClick={props.onInviteClick}>
        <img src={personPlusIcon} alt='' aria-hidden='true' />
      </button>
    </div>
  </div>
}

// Drawn inline rather than imported as image assets so they take the button's
// text colour, which keeps them tinted -- and right in dark mode -- without a
// second copy of each icon.
const ICON_PROPS = {
  viewBox: '0 0 24 24',
  width: 16,
  height: 16,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true
} as const;

// lucide: tag
function TagIcon() {
  return <svg {...ICON_PROPS}>
    <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
    <circle cx="7.5" cy="7.5" r="1" fill="currentColor" />
  </svg>
}

// lucide: pencil
function PencilIcon() {
  return <svg {...ICON_PROPS}>
    <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
  </svg>
}

// lucide: chevron-right
function ChevronForwardIcon() {
  return <svg {...ICON_PROPS} width={14} height={14}>
    <path d="m9 18 6-6-6-6" />
  </svg>
}
